import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useSocket } from '../hooks/useSocket.jsx';
import { Avatar } from '../components/Avatar.jsx';
import { AuthenticatedImage } from '../components/AuthenticatedImage.jsx';

export default function AdminPresencePage() {
    const queryClient = useQueryClient();
    const { connected, subscribe } = useSocket();
    const [reconnectFlash, setReconnectFlash] = useState(false);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['presence', 'team'],
        queryFn: () => api.get('/api/v1/dashboard/team-presence'),
        staleTime: Infinity,
    });

    useEffect(() => {
        const updateEmployee = (employeeId, patch) => {
            queryClient.setQueryData(['presence', 'team'], (prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    data: prev.data.map((row) => {
                        if (row.employee.id !== employeeId) return row;
                        return {
                            ...row,
                            ...(patch.status ? { status: patch.status } : {}),
                            today: {
                                ...row.today,
                                ...(patch.today_hours !== undefined ? { hours: patch.today_hours } : {}),
                                ...(patch.is_late_today !== undefined ? { is_late_today: patch.is_late_today } : {}),
                                ...(patch.late_minutes !== undefined ? { late_minutes: patch.late_minutes } : {}),
                            },
                        };
                    }),
                };
            });
        };

        const offPresence = subscribe('presence:update', (e) => {
            updateEmployee(e.employee_id, { status: e.status, today_hours: e.today_hours });
        });
        const offCheckedIn = subscribe('attendance:checked_in', (e) => {
            updateEmployee(e.employee_id, {
                status: 'online',
                is_late_today: e.is_late,
                late_minutes: e.late_minutes,
            });
        });
        const offCheckedOut = subscribe('attendance:checked_out', (e) => {
            updateEmployee(e.employee_id, {
                status: 'offline',
                today_hours: e.total_hours,
            });
        });

        return () => { offPresence(); offCheckedIn(); offCheckedOut(); };
    }, [subscribe, queryClient]);

    useEffect(() => {
        if (connected) {
            refetch();
            setReconnectFlash(true);
            const t = setTimeout(() => setReconnectFlash(false), 1500);
            return () => clearTimeout(t);
        }
    }, [connected, refetch]);

    return (
        <>
            <header className="flex items-center justify-between" style={{ marginBottom: 16 }}>
                <div className="flex items-center" style={{ gap: 12 }}>
                    <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Live presence</h1>
                    <ConnectionDot connected={connected} flash={reconnectFlash} />
                </div>
                <button onClick={() => refetch()} className="btn btn-soft">Refresh</button>
            </header>

            {isLoading && <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>}
            {isError && <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>Could not load team presence.</p>}

            {data && (
                <>
                    <div
                        className="grid"
                        style={{
                            gap: 12,
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        }}
                    >
                        {data.data.length === 0 ? (
                            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', gridColumn: '1 / -1' }}>
                                No employees in your scope.
                            </p>
                        ) : (
                            data.data.map((row) => <PresenceCard key={row.employee.id} row={row} />)
                        )}
                    </div>

                    <p
                        style={{
                            fontSize: 11,
                            color: 'var(--color-text-tertiary)',
                            marginTop: 24,
                        }}
                    >
                        Updates live via Socket.io. {data.data.length}{' '}
                        {data.data.length === 1 ? 'employee' : 'employees'} in view.
                    </p>
                </>
            )}
        </>
    );
}

function PresenceCard({ row }) {
    const { employee, status, today, latest_screenshot: latest } = row;
    return (
        <Link
            to={`/admin/employees/${employee.id}`}
            className="surface-card flex flex-col"
            style={{
                padding: 14,
                gap: 10,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'border-color 120ms ease, box-shadow 120ms ease',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-status-complete)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border-tertiary)';
            }}
        >
            <div className="flex items-start" style={{ gap: 12 }}>
                <Avatar user={employee} size="md" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center" style={{ gap: 8 }}>
                        <p
                            style={{
                                fontSize: 14,
                                fontWeight: 500,
                                margin: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {employee.name}
                        </p>
                        <StatusDot status={status} />
                    </div>
                    <p
                        style={{
                            fontSize: 11,
                            color: 'var(--color-text-tertiary)',
                            margin: 0,
                            marginTop: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {employee.department} · {employee.role}
                    </p>
                    <div className="flex items-center" style={{ marginTop: 8, gap: 8, fontSize: 12 }}>
                        <HoursLabel hours={today?.hours ?? 0} />
                        {today?.is_late_today && (
                            <span className="pill pill-pending">
                                Late {today.late_minutes ? `${today.late_minutes}m` : ''}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <LatestScreenshot latest={latest} role={employee.role} />
        </Link>
    );
}

function LatestScreenshot({ latest, role }) {
    // Managers and admins have no agent, so the column shows a placeholder.
    if (role !== 'employee') {
        return (
            <div
                style={{
                    padding: '8px 10px',
                    fontSize: 11,
                    color: 'var(--color-text-tertiary)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--border-radius-sm)',
                    textAlign: 'center',
                }}
            >
                No agent — browser-based presence only
            </div>
        );
    }
    if (!latest) {
        return (
            <div
                style={{
                    padding: '14px 10px',
                    fontSize: 11,
                    color: 'var(--color-text-tertiary)',
                    background: 'var(--color-bg-secondary)',
                    border: '0.5px dashed var(--color-border-tertiary)',
                    borderRadius: 'var(--border-radius-sm)',
                    textAlign: 'center',
                }}
            >
                No screenshot today
            </div>
        );
    }
    return (
        <div
            style={{
                position: 'relative',
                aspectRatio: '16 / 9',
                background: '#000',
                borderRadius: 'var(--border-radius-sm)',
                overflow: 'hidden',
                border: '0.5px solid var(--color-border-tertiary)',
            }}
            title={`${latest.active_app || '(no app)'} · ${new Date(latest.timestamp).toLocaleTimeString()}`}
        >
            <AuthenticatedImage
                screenshotId={latest.id}
                alt={latest.active_app}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                }}
            />
            {!latest.viewed_by_me && (
                <span
                    title="Not yet viewed"
                    style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--color-status-today)',
                        boxShadow: '0 0 0 2px rgba(255,255,255,0.6)',
                    }}
                />
            )}
            <div
                style={{
                    position: 'absolute',
                    left: 0, right: 0, bottom: 0,
                    padding: '4px 8px',
                    fontSize: 11,
                    color: '#fff',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {latest.active_app || ''} · {new Date(latest.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
        </div>
    );
}

function StatusDot({ status }) {
    const config = {
        online:   { bg: 'var(--color-status-complete)',    label: 'Online' },
        away:     { bg: 'var(--color-status-incomplete)',  label: 'Away' },
        offline:  { bg: 'var(--color-status-weekend)',     label: 'Offline' },
        on_leave: { bg: 'var(--color-status-on-leave)',    label: 'On leave' },
        holiday:  { bg: 'var(--color-status-on-leave)',    label: 'Holiday' },
    };
    const c = config[status] || config.offline;
    return (
        <span title={c.label} className="inline-flex items-center" style={{ gap: 4 }}>
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: c.bg,
                }}
            />
            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{c.label}</span>
        </span>
    );
}

function HoursLabel({ hours }) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return (
        <span className="tnum" style={{ color: 'var(--color-text-secondary)' }}>
            {h}h {m.toString().padStart(2, '0')}m today
        </span>
    );
}

function ConnectionDot({ connected, flash }) {
    if (connected) {
        return (
            <span
                className="inline-flex items-center"
                style={{ gap: 6, fontSize: 11, color: 'var(--color-pill-approved-fg)' }}
            >
                <span
                    className={flash ? 'animate-pulse' : ''}
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--color-status-complete)',
                    }}
                />
                {flash ? 'Reconnected' : 'Live'}
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center"
            style={{ gap: 6, fontSize: 11, color: 'var(--color-text-tertiary)' }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'var(--color-status-weekend)',
                }}
            />
            Offline (will reconnect)
        </span>
    );
}
